import { useEffect, useState } from "react";
import { Grid, Link, Typography } from "@material-ui/core";
import { EmptyState, InfoCard, Progress } from "@backstage/core-components";
import { useEntity } from "@backstage/plugin-catalog-react";
import { getGitlabPipelinesApiPath, getGitlabProjectApiPath, getGitlabProjectSlug } from "./gitlab";

type GitlabProject = {
  web_url: string;
};

type GitlabPipeline = {
  id: number;
  status: string;
  ref: string;
  sha: string;
  updated_at: string;
  web_url: string;
};

type GitlabCiCdState = {
  loading: boolean;
  error?: string;
  projectWebUrl?: string;
  pipeline?: GitlabPipeline;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return "Unknown error";
};

export const GitlabCiCdCard = () => {
  const { entity } = useEntity();
  const projectSlug = getGitlabProjectSlug(entity);

  const [state, setState] = useState<GitlabCiCdState>({
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!projectSlug) {
        setState({
          loading: false,
          error: "gitlab.com/project-slug annotation is missing",
        });
        return;
      }

      try {
        const projectResponse = await fetch(getGitlabProjectApiPath(projectSlug));
        if (!projectResponse.ok) {
          throw new Error(`Project request failed: ${projectResponse.status}`);
        }
        const project = (await projectResponse.json()) as GitlabProject;

        const pipelinesResponse = await fetch(getGitlabPipelinesApiPath(projectSlug));
        if (!pipelinesResponse.ok) {
          throw new Error(`Pipelines request failed: ${pipelinesResponse.status}`);
        }
        const pipelines = (await pipelinesResponse.json()) as GitlabPipeline[];

        if (!cancelled) {
          setState({
            loading: false,
            projectWebUrl: project.web_url,
            pipeline: pipelines[0],
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            loading: false,
            error: getErrorMessage(error),
          });
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [projectSlug]);

  if (state.loading) {
    return (
      <InfoCard title="GitLab CI/CD">
        <Progress />
      </InfoCard>
    );
  }

  if (state.error) {
    return (
      <InfoCard title="GitLab CI/CD">
        <EmptyState
          title="Unable to load GitLab pipelines"
          missing="info"
          description={state.error}
        />
      </InfoCard>
    );
  }

  if (!state.projectWebUrl || !state.pipeline) {
    return (
      <InfoCard title="GitLab CI/CD">
        <EmptyState
          title="No pipelines found"
          missing="data"
          description="No pipeline runs were returned by GitLab for this project."
        />
      </InfoCard>
    );
  }

  return (
    <InfoCard title="GitLab CI/CD">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            Project:{" "}
            <Link
              href={`${state.projectWebUrl}/-/pipelines`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {projectSlug}
            </Link>
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1">
            Latest pipeline:{" "}
            <Link href={state.pipeline.web_url} target="_blank" rel="noopener noreferrer">
              #{state.pipeline.id}
            </Link>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Status: {state.pipeline.status}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Ref: {state.pipeline.ref}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Commit:{" "}
            <Link
              href={`${state.projectWebUrl}/-/commit/${state.pipeline.sha}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {state.pipeline.sha.slice(0, 8)}
            </Link>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Updated: {new Date(state.pipeline.updated_at).toLocaleString()}
          </Typography>
        </Grid>
      </Grid>
    </InfoCard>
  );
};
